// Utils
import { isOnline } from './Connectivity';
import { getOfflineServices } from './OfflineServices';
import { isOfflineEnabled } from './OfflineFlag';
import { LANES, OP_STATUS, targetKeyOf } from './Outbox';
import { addPendingKeys } from './SyncState';
import { showToast } from '../ToastsUtil';

// Façade d'écriture : chemin unique d'une saisie de terrain, en ligne comme hors-ligne (copie locale, file, moteur)

// Attente maximale de la réponse de l'API ; ensuite l'opération continue en arrière-plan
export const LOCAL_WAIT_MS = 5000;

// N'attend que si l'opération peut partir tout de suite (en ligne, rien devant elle dans sa voie) ; les photos n'attendent jamais
const getWaitMs = async (services, queued) => {
    if (!isOnline() || queued.lane === LANES.FILES) return 0;
    const ahead = (await services.outbox.list())
        .filter(o => o.lane === queued.lane && o.seq < queued.seq && o.status !== OP_STATUS.FAILED && o.status !== OP_STATUS.BLOCKED);
    return ahead.length ? 0 : LOCAL_WAIT_MS;
};

// Pilote et moteur démarré ; direct : écran hors terrain qui appelle l'API sans passer par la file
export const canUseFieldWrite = ({ direct = false } = {}) => !direct && isOfflineEnabled() && !!getOfflineServices()?.engine?.isStarted();

// Applique la saisie à la copie locale, la met en file et attend son issue : réponse de l'API si elle arrive à temps,
// sinon { data, isLocal: true } ; un refus de l'API annule la saisie et relance l'erreur
export const submitFieldWrite = async ({ op, applyLocal, revertLocal, buildLocalData }) => {
    const services = getOfflineServices();
    let queued, merged;
    // Avant la copie et la file : un message temps réel arrivé entre-temps ne remplace pas l'affichage de la saisie
    addPendingKeys(op.touches || [targetKeyOf(op.target)]);
    try {
        if (applyLocal) await applyLocal(services);
        ({ op: queued, merged } = await services.outbox.enqueue(op));
    } catch (error) {
        // Appareil plein : la saisie n'est pas gardée (Dexie enveloppe parfois l'erreur du navigateur dans inner)
        if (error?.name === 'QuotaExceededError' || error?.inner?.name === 'QuotaExceededError') showToast('storage_full');
        if (revertLocal) await revertLocal(services).catch(console.error);
        throw error;
    }
    const outcome = await services.engine.sendNow(queued.id, { waitMs: await getWaitMs(services, queued) });
    if (outcome.status === 'acked') return outcome.response;
    if (outcome.status === 'failed') {
        // Opération fusionnée : la retirer effacerait des saisies plus anciennes, elle reste en échec (écran Synchronisation)
        if (!merged) {
            // Comme un abandon : chaque saisie retirée avec elle est aussi annulée sur l'appareil (onAbandon)
            await services.engine.abandon(queued.id);
            if (revertLocal) await revertLocal(services).catch(console.error);
        }
        throw outcome.error;
    }
    return { data: buildLocalData ? buildLocalData() : null, isLocal: true, discarded: outcome.status === 'discarded' };
};
